// Public registration page — opened from the email "Je m'inscris" button.
// Three steps in one page:
//   1) Verify personal details (prefilled from the pre-registration)
//   2) Add companions (one row per accompanying person)
//   3) Pick paid pricing items (passes, meals, workshops, …) — pulled from
//      /api/presence/pricing, set by admins in APLEO → Fêtes 2027 → Tarifs
// On submit:
//   • total == 0 → POST /presence/inscription/{token}/checkout (confirms immediately)
//   • total > 0  → POST /presence/inscription/{token}/checkout → SumUp hosted URL
// "Je ne pourrai pas venir" → POST /presence/inscription/{token}/decline
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";

const AGE_BUCKETS = [
  { v: "under_6", fr: "Moins de 6 ans", en: "Under 6" },
  { v: "6_12",    fr: "6 – 12 ans",     en: "6 – 12" },
  { v: "13_17",   fr: "13 – 17 ans",    en: "13 – 17" },
  { v: "adult",   fr: "Adulte",         en: "Adult" },
  { v: "senior",  fr: "65 ans et +",    en: "65+" },
];

const CATEGORY_LABELS = {
  pass:       { fr: "Passes",          en: "Passes" },
  repas:      { fr: "Repas",           en: "Meals" },
  atelier:    { fr: "Ateliers",        en: "Workshops" },
  conference: { fr: "Conférences",     en: "Conferences" },
  autre:      { fr: "Autres",          en: "Other" },
};
const CATEGORY_ORDER = ["pass", "repas", "atelier", "conference", "autre"];

const HERO = "/photos/prieure-meadow.jpg";

export default function Inscription() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isDeclineUrl = location.pathname.endsWith("/decline");
  const { locale } = useI18n();
  const isFr = locale === "fr";
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(null);
  const [companions, setCompanions] = useState([]);
  const [notes, setNotes] = useState("");
  const [pricing, setPricing] = useState([]);
  const [quantities, setQuantities] = useState({}); // pricing_id -> qty
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // 'confirmed' | 'declined' | null
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;
    if (isDeclineUrl) {
      api.post(`/presence/inscription/${token}/decline`)
        .then(() => mounted && setDone("declined"))
        .catch(() => mounted && setNotFound(true))
        .finally(() => mounted && setLoading(false));
      return;
    }
    api.get(`/presence/inscription/${token}`)
      .then((r) => {
        if (!mounted) return;
        const d = r.data;
        setForm({
          first_name: d.first_name || "",
          last_name: d.last_name || "",
          email: d.email || "",
          phone: d.phone || "",
          country: d.country || "France",
          city: d.city || "",
        });
        const ext = [];
        const push = (n, bucket) => { for (let i = 0; i < n; i++) ext.push({ first_name: "", last_name: "", age_bucket: bucket }); };
        push(d.nb_under_6 || 0, "under_6");
        push(d.nb_6_12 || 0, "6_12");
        push(d.nb_13_17 || 0, "13_17");
        push(Math.max((d.nb_adult || 0) - 1, 0), "adult");
        push(d.nb_senior || 0, "senior");
        setCompanions(ext);
        setPricing(d.pricing || []);
        // Prefill quantities from previously saved registration_lines (if any)
        const q = {};
        (d.registration_lines || []).forEach((ln) => { if (ln.pricing_id) q[ln.pricing_id] = ln.quantity; });
        setQuantities(q);
      })
      .catch(() => mounted && setNotFound(true))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [token, isDeclineUrl]);

  const updateCompanion = (i, k, v) =>
    setCompanions((arr) => arr.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const addCompanion = () =>
    setCompanions((arr) => [...arr, { first_name: "", last_name: "", age_bucket: "adult" }]);
  const removeCompanion = (i) =>
    setCompanions((arr) => arr.filter((_, idx) => idx !== i));

  const setQty = (id, q) => setQuantities((m) => ({ ...m, [id]: Math.max(0, Number(q) || 0) }));

  const total = useMemo(() => {
    return Math.round(
      pricing.reduce((sum, p) => sum + (Number(p.price_eur) || 0) * (quantities[p.id] || 0), 0) * 100,
    ) / 100;
  }, [pricing, quantities]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setErrorMsg("");
    try {
      const selections = Object.entries(quantities)
        .filter(([, q]) => Number(q) > 0)
        .map(([pricing_id, quantity]) => ({ pricing_id, quantity: Number(quantity) }));
      const payload = {
        ...form,
        notes,
        companions: companions.filter((c) => c.first_name.trim() || c.last_name.trim()),
        selections,
      };
      const { data } = await api.post(`/presence/inscription/${token}/checkout`, payload);
      if (data.free) {
        setDone("confirmed");
        return;
      }
      if (data.url) {
        // Redirect to SumUp hosted checkout
        window.location.href = data.url;
        return;
      }
      setErrorMsg(isFr ? "Une erreur est survenue. Réessayez dans un instant." : "An error occurred. Please try again.");
    } catch (err) {
      const msg = err?.response?.data?.detail || (isFr ? "Erreur lors de la confirmation." : "Confirmation error.");
      setErrorMsg(msg);
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!window.confirm(isFr
      ? "Confirmer que vous ne pourrez pas être des nôtres ?"
      : "Confirm you will not be able to join us?")) return;
    setBusy(true);
    try {
      await api.post(`/presence/inscription/${token}/decline`);
      setDone("declined");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="py-32 text-center font-display italic text-xl" style={{ color: "var(--muted)" }}>…</div>
    );
  }

  if (notFound) {
    return (
      <div className="py-24" style={{ background: "var(--parchment-2)", minHeight: "calc(100vh - 12rem)" }}>
        <div className="container-prieure max-w-2xl text-center">
          <div className="bg-white border p-12" style={{ borderColor: "var(--border)" }}>
            <Ornament className="mb-6" />
            <h1 className="font-display text-3xl mb-3">
              {isFr ? "Lien invalide ou expiré" : "Invalid or expired link"}
            </h1>
            <p className="font-body" style={{ color: "var(--ink-2)" }}>
              {isFr
                ? "Ce lien d'inscription ne semble plus valide. Si vous pensez que c'est une erreur, n'hésitez pas à nous écrire."
                : "This registration link no longer seems valid. If you think this is a mistake, feel free to contact us."}
            </p>
            <button onClick={() => navigate("/contact")} className="btn btn-outline mt-6">
              {isFr ? "Nous écrire" : "Contact us"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (done === "confirmed") {
    return (
      <DoneCard
        title={isFr ? "Merci, votre inscription est enregistrée" : "Thank you, your registration is recorded"}
        body={isFr
          ? "Nous vous écrirons à nouveau quelques semaines avant l'événement avec toutes les informations pratiques."
          : "We will write to you again a few weeks before the event with all practical information."}
        ornament="✓"
      />
    );
  }
  if (done === "declined") {
    return (
      <DoneCard
        title={isFr ? "Bien noté, et merci de nous avoir prévenus" : "Noted — thank you for letting us know"}
        body={isFr
          ? "Nous comprenons. Vous restez dans nos pensées, et nous serions ravis de vous accueillir une autre fois au prieuré."
          : "We understand. You remain in our thoughts, and we would be delighted to welcome you to the priory another time."}
        ornament="·"
      />
    );
  }

  // Group pricing by category for display
  const groups = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: pricing.filter((p) => (p.category || "autre") === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <div data-testid="inscription-page">
      <section className="relative" style={{ height: "32vh", minHeight: 240 }}>
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-10">
          <div className="font-caps text-[0.7rem] text-white/90 mb-3" style={{ letterSpacing: "0.28em" }}>
            {isFr ? "Inscription définitive" : "Final registration"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white">
            {isFr ? "Confirmation de votre venue" : "Confirm your attendance"}
          </h1>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--parchment-2)" }}>
        <div className="container-prieure max-w-4xl">
          <p className="font-display italic text-lg text-center mb-10" style={{ color: "var(--ink-2)" }}>
            {isFr
              ? "Vérifiez vos coordonnées, complétez le nom des personnes qui vous accompagnent, choisissez ce à quoi vous souhaitez participer, puis procédez au paiement."
              : "Check your details, fill in the names of those joining you, choose what you wish to take part in, then proceed to payment."}
          </p>

          <form onSubmit={submit}
                className="bg-white border p-6 sm:p-10 space-y-8"
                style={{ borderColor: "var(--border)" }}>
            {/* Respondent details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={isFr ? "Prénom *" : "First name *"} testId="insc-first-name"
                     value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required />
              <Field label={isFr ? "Nom *" : "Last name *"} testId="insc-last-name"
                     value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required />
              <Field label="Email *" testId="insc-email" type="email"
                     value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <Field label={isFr ? "Téléphone" : "Phone"} testId="insc-phone" type="tel"
                     value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label={isFr ? "Pays *" : "Country *"} testId="insc-country"
                     value={form.country} onChange={(v) => setForm({ ...form, country: v })} required />
              <Field label={isFr ? "Ville *" : "City *"} testId="insc-city"
                     value={form.city} onChange={(v) => setForm({ ...form, city: v })} required />
            </div>

            {/* Companions */}
            <div>
              <div className="flex justify-between items-baseline mb-3 flex-wrap gap-2">
                <div className="font-caps text-[0.65rem]" style={{ color: "var(--ink)", letterSpacing: "0.22em" }}>
                  {isFr ? "PERSONNES QUI VOUS ACCOMPAGNENT" : "PEOPLE JOINING YOU"}
                </div>
                <button type="button" onClick={addCompanion} data-testid="insc-add-companion"
                        className="font-caps text-[0.6rem] px-3 py-1.5 border"
                        style={{ borderColor: "var(--ink)", color: "var(--ink)", letterSpacing: "0.18em" }}>
                  + {isFr ? "AJOUTER" : "ADD"}
                </button>
              </div>
              {companions.length === 0 ? (
                <p className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
                  {isFr ? "Vous venez seul·e — cliquez sur AJOUTER si quelqu'un vous accompagne." : "You're coming on your own — click ADD if someone is joining you."}
                </p>
              ) : (
                <div className="space-y-3">
                  {companions.map((c, i) => (
                    <div key={i} data-testid={`insc-companion-${i}`}
                         className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end p-3"
                         style={{ background: "var(--parchment-2)" }}>
                      <div className="sm:col-span-4">
                        <span className="font-body text-xs" style={{ color: "var(--muted)" }}>{isFr ? "Prénom" : "First name"}</span>
                        <input className="input-classic mt-1 w-full"
                               value={c.first_name}
                               onChange={(e) => updateCompanion(i, "first_name", e.target.value)} />
                      </div>
                      <div className="sm:col-span-4">
                        <span className="font-body text-xs" style={{ color: "var(--muted)" }}>{isFr ? "Nom" : "Last name"}</span>
                        <input className="input-classic mt-1 w-full"
                               value={c.last_name}
                               onChange={(e) => updateCompanion(i, "last_name", e.target.value)} />
                      </div>
                      <div className="sm:col-span-3">
                        <span className="font-body text-xs" style={{ color: "var(--muted)" }}>{isFr ? "Tranche d'âge" : "Age group"}</span>
                        <select className="input-classic mt-1 w-full" style={{ background: "transparent" }}
                                value={c.age_bucket}
                                onChange={(e) => updateCompanion(i, "age_bucket", e.target.value)}>
                          {AGE_BUCKETS.map((b) => (
                            <option key={b.v} value={b.v}>{isFr ? b.fr : b.en}</option>
                          ))}
                        </select>
                      </div>
                      <button type="button" onClick={() => removeCompanion(i)}
                              data-testid={`insc-companion-remove-${i}`}
                              className="sm:col-span-1 font-caps text-[0.6rem] py-2"
                              style={{ color: "var(--cinnabar)", letterSpacing: "0.18em" }}>
                        {isFr ? "RETIRER" : "REMOVE"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pricing items */}
            {groups.length > 0 && (
              <div className="space-y-5" data-testid="insc-pricing">
                <div className="font-caps text-[0.65rem]" style={{ color: "var(--ink)", letterSpacing: "0.22em" }}>
                  {isFr ? "VOTRE PARTICIPATION" : "YOUR PARTICIPATION"}
                </div>
                {groups.map((g) => (
                  <div key={g.cat}>
                    <div className="font-caps text-[0.6rem] mb-2" style={{ color: "var(--cinnabar)", letterSpacing: "0.2em" }}>
                      ◆ {isFr ? CATEGORY_LABELS[g.cat].fr : CATEGORY_LABELS[g.cat].en}
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      {g.items.map((p) => {
                        const label = isFr ? ((p.label || {}).fr || (p.label || {}).en) : ((p.label || {}).en || (p.label || {}).fr);
                        const desc  = isFr ? ((p.description || {}).fr || "") : ((p.description || {}).en || "");
                        return (
                          <div key={p.id} data-testid={`insc-pricing-${p.code}`}
                               className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center p-3"
                               style={{ background: "var(--parchment-2)" }}>
                            <div className="sm:col-span-7">
                              <div className="font-display">{label}</div>
                              {desc && <div className="text-xs italic" style={{ color: "var(--muted)" }}>{desc}</div>}
                            </div>
                            <div className="sm:col-span-2 text-right font-display">{Number(p.price_eur).toFixed(2)} €</div>
                            <div className="sm:col-span-3 flex items-center justify-end gap-2">
                              <span className="font-caps text-[0.6rem]" style={{ color: "var(--muted)" }}>QTÉ</span>
                              <input type="number" min={0} max={50}
                                     data-testid={`insc-qty-${p.code}`}
                                     value={quantities[p.id] || 0}
                                     onChange={(e) => setQty(p.id, e.target.value)}
                                     className="input-classic w-20 text-center" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="flex justify-between items-baseline pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                  <span className="font-caps" style={{ letterSpacing: "0.22em" }}>{isFr ? "TOTAL À RÉGLER" : "TOTAL DUE"}</span>
                  <span data-testid="insc-total" className="font-display text-3xl" style={{ color: total > 0 ? "var(--cinnabar)" : "var(--moss)" }}>
                    {total.toFixed(2)} €
                  </span>
                </div>
              </div>
            )}

            {/* Notes */}
            <label className="block">
              <span className="label-caps">
                {isFr ? "Précisions (régime alimentaire, accessibilité, attentes…)" : "Notes (diet, accessibility, expectations…)"}
              </span>
              <textarea data-testid="insc-notes" rows={3}
                        value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="input-classic mt-1 w-full" />
            </label>

            {errorMsg && (
              <div data-testid="insc-error" className="p-3 text-sm"
                   style={{ background: "rgba(153,27,27,0.08)", color: "var(--cinnabar)", border: "1px solid var(--cinnabar)" }}>
                {errorMsg}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between items-center flex-wrap gap-3 pt-2">
              <button type="button" onClick={decline} disabled={busy}
                      data-testid="insc-decline"
                      className="font-caps text-[0.65rem] px-4 py-2 border"
                      style={{ borderColor: "var(--muted)", color: "var(--muted)", letterSpacing: "0.18em" }}>
                {isFr ? "Je ne pourrai pas venir" : "I will not be able to come"}
              </button>
              <button type="submit" disabled={busy}
                      data-testid="insc-confirm"
                      className="btn btn-primary">
                {busy ? "…" : total > 0
                  ? (isFr ? `Payer ${total.toFixed(2)} € — SumUp` : `Pay ${total.toFixed(2)} € — SumUp`)
                  : (isFr ? "Confirmer mon inscription" : "Confirm my registration")}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", required = false }) {
  return (
    <label className="block">
      <span className="label-caps">{label}</span>
      <input data-testid={testId} type={type} required={required}
             value={value} onChange={(e) => onChange(e.target.value)}
             className="input-classic mt-1 w-full" />
    </label>
  );
}

function DoneCard({ title, body, ornament }) {
  return (
    <div data-testid="insc-done" className="py-24" style={{ background: "var(--parchment-2)", minHeight: "calc(100vh - 12rem)" }}>
      <div className="container-prieure max-w-2xl text-center">
        <div className="bg-white border p-12" style={{ borderColor: "var(--border)" }}>
          <div className="font-display text-6xl mb-6" style={{ color: "var(--moss)" }}>{ornament}</div>
          <h1 className="font-display text-3xl mb-4">{title}</h1>
          <p className="font-body text-lg" style={{ color: "var(--ink-2)" }}>{body}</p>
        </div>
      </div>
    </div>
  );
}
